import axios, { AxiosInstance, AxiosRequestConfig } from 'axios'
import { deepseekService, DeepSeekMessage, DeepSeekResponse } from './deepseek-service'
import { costOptimizer } from './cost-optimizer'

export interface CacheEntry {
  timestamp: number
  data: DeepSeekResponse
  ttl: number
}

export interface BatchRequest {
  id: string
  messages: DeepSeekMessage[]
  options?: {
    temperature?: number
    maxTokens?: number
  }
  resolve: (response: DeepSeekResponse) => void
  reject: (error: any) => void
}

export interface OptimizedConfig {
  enableCache?: boolean
  cacheTTL?: number
  enableBatching?: boolean
  batchTimeout?: number
  maxBatchSize?: number
  enableRetry?: boolean
  maxRetries?: number
  retryDelay?: number
  enableRateLimit?: boolean
  maxRequestsPerSecond?: number
}

class DeepSeekOptimizedService {
  private cache: Map<string, CacheEntry> = new Map()
  private pendingBatch: BatchRequest[] = []
  private batchTimer: number | null = null
  private requestCount = 0
  private lastRequestTime = 0
  private axiosInstance: AxiosInstance
  private config: OptimizedConfig
  private cacheHits = 0
  private cacheMisses = 0
  private totalResponseTime = 0
  private errorCount = 0
  private apiKey: string
  private apiConfigured: boolean

  isConfigured(): boolean {
    return this.apiConfigured
  }

  constructor(config: OptimizedConfig = {}) {
    this.config = {
      enableCache: config.enableCache ?? true,
      cacheTTL: config.cacheTTL ?? 300000,
      enableBatching: config.enableBatching ?? true,
      batchTimeout: config.batchTimeout ?? 100,
      maxBatchSize: config.maxBatchSize ?? 10,
      enableRetry: config.enableRetry ?? true,
      maxRetries: config.maxRetries ?? 3,
      retryDelay: config.retryDelay ?? 1000,
      enableRateLimit: config.enableRateLimit ?? true,
      maxRequestsPerSecond: config.maxRequestsPerSecond ?? 10
    }

    this.apiKey = import.meta.env.VITE_DEEPSEEK_API_KEY || 'sk-xxxxxxxx'
    this.apiConfigured = !!(this.apiKey && this.apiKey !== 'sk-xxxxxxxx' && this.apiKey !== 'your-api-key-here')

    this.axiosInstance = axios.create({
      baseURL: import.meta.env.VITE_DEEPSEEK_API_URL || 'https://api.deepseek.com',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.apiKey}`
      },
      timeout: 60000,
      keepAlive: true,
      maxRedirects: 5
    })

    // 请求拦截器：速率限制
    this.axiosInstance.interceptors.request.use(async (config) => {
      if (this.config.enableRateLimit) {
        await this.rateLimit()
      }
      return config
    })

    // 响应拦截器：自动重试
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (this.config.enableRetry && error.response?.status >= 500) {
          const retryCount = error.config._retryCount || 0
          if (retryCount < this.config.maxRetries!) {
            error.config._retryCount = retryCount + 1
            await this.delay(this.config.retryDelay! * Math.pow(2, retryCount))
            return this.axiosInstance(error.config)
          }
        }
        return Promise.reject(error)
      }
    )
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  private async rateLimit(): Promise<void> {
    const now = Date.now()
    const elapsed = now - this.lastRequestTime
    
    if (elapsed < 1000 / this.config.maxRequestsPerSecond!) {
      await this.delay(1000 / this.config.maxRequestsPerSecond! - elapsed)
    }
    
    this.lastRequestTime = Date.now()
    this.requestCount++
  }

  private generateCacheKey(messages: DeepSeekMessage[], options?: { temperature?: number; maxTokens?: number }): string {
    const key = JSON.stringify({
      messages,
      options: {
        temperature: options?.temperature ?? 0.7,
        maxTokens: options?.maxTokens ?? 2048
      }
    })
    return this.hashString(key)
  }

  private hashString(str: string): string {
    let hash = 0
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash
    }
    return Math.abs(hash).toString(36)
  }

  private getCachedResponse(messages: DeepSeekMessage[], options?: { temperature?: number; maxTokens?: number }): DeepSeekResponse | null {
    if (!this.config.enableCache) return null

    const key = this.generateCacheKey(messages, options)
    const entry = this.cache.get(key)
    
    if (entry && Date.now() - entry.timestamp < entry.ttl) {
      entry.accessCount++
      entry.lastAccessTime = Date.now()
      this.cacheHits++
      costOptimizer.recordCacheResult(true)
      console.log(`Cache hit for request: ${key}, access count: ${entry.accessCount}`)
      return entry.data
    }
    
    if (entry) {
      this.cache.delete(key)
    }
    
    this.cacheMisses++
    costOptimizer.recordCacheResult(false)
    return null
  }

  private cacheResponse(messages: DeepSeekMessage[], options: { temperature?: number; maxTokens?: number }, data: DeepSeekResponse): void {
    if (!this.config.enableCache) return

    const key = this.generateCacheKey(messages, options)
    this.cache.set(key, {
      timestamp: Date.now(),
      data,
      ttl: this.config.cacheTTL!,
      accessCount: 1,
      lastAccessTime: Date.now()
    })

    this.enforceCacheLimits()
  }

  private enforceCacheLimits(): void {
    const maxCacheSize = 1000
    
    if (this.cache.size <= maxCacheSize) return

    const sortedEntries = Array.from(this.cache.entries()).sort((a, b) => {
      const accessDiff = b[1].accessCount - a[1].accessCount
      if (accessDiff !== 0) return accessDiff
      return a[1].lastAccessTime - b[1].lastAccessTime
    })

    const toRemove = sortedEntries.slice(maxCacheSize)
    toRemove.forEach(([key]) => {
      this.cache.delete(key)
    })
    
    console.log(`Cache evicted ${toRemove.length} entries, current size: ${this.cache.size}`)
  }

  private async cleanupExpiredCache(): Promise<void> {
    const now = Date.now()
    let removedCount = 0
    
    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.timestamp >= entry.ttl) {
        this.cache.delete(key)
        removedCount++
      }
    }
    
    if (removedCount > 0) {
      console.log(`Cleaned up ${removedCount} expired cache entries`)
    }
  }

  private async processBatch(): Promise<void> {
    if (this.pendingBatch.length === 0) return

    const batch = [...this.pendingBatch]
    this.pendingBatch = []
    this.batchTimer = null

    console.log(`Processing batch of ${batch.length} requests`)

    // 并行处理批处理请求
    const promises = batch.map(async (request) => {
      try {
        const cached = this.getCachedResponse(request.messages, request.options)
        if (cached) {
          request.resolve(cached)
          return
        }

        const response = await deepseekService.chat(request.messages, request.options)
        this.cacheResponse(request.messages, request.options || {}, response)
        request.resolve(response)
      } catch (error) {
        request.reject(error)
      }
    })

    await Promise.all(promises)
  }

  private addToBatch(request: BatchRequest): void {
    this.pendingBatch.push(request)

    if (this.pendingBatch.length >= this.config.maxBatchSize!) {
      this.processBatch()
    } else if (!this.batchTimer) {
      this.batchTimer = window.setTimeout(() => {
        this.processBatch()
      }, this.config.batchTimeout!)
    }
  }

  async chat(messages: DeepSeekMessage[], options?: {
    temperature?: number
    maxTokens?: number
    topP?: number
    frequencyPenalty?: number
    presencePenalty?: number
  }): Promise<DeepSeekResponse> {
    if (!this.isConfigured()) {
      return {
        id: 'empty_' + Date.now(),
        object: 'chat.completion',
        created: Date.now(),
        model: 'local-fallback',
        choices: [{ index: 0, message: { role: 'assistant', content: '' }, finish_reason: 'stop' }],
        usage: { prompt_tokens: 0, completion_tokens: 0, total_tokens: 0 }
      }
    }
    const startTime = Date.now()
    this.requestCount++

    try {
      const cached = this.getCachedResponse(messages, options)
      if (cached) {
        this.totalResponseTime += Date.now() - startTime
        return cached
      }

      if (this.config.enableBatching) {
        return new Promise((resolve, reject) => {
          this.addToBatch({
            id: `batch_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            messages,
            options: { temperature: options?.temperature, maxTokens: options?.maxTokens },
            resolve: (data) => {
              this.totalResponseTime += Date.now() - startTime
              resolve(data)
            },
            reject: (error) => {
              this.errorCount++
              reject(error)
            }
          })
        })
      }

      const response = await deepseekService.chat(messages, options)
      
      // 记录API使用成本
      const responseText = response.choices[0]?.message?.content || ''
      const promptTokens = this.estimateTokens(messages)
      const completionTokens = this.estimateTokens([{ role: 'assistant', content: responseText }])
      costOptimizer.recordApiCall({ input: promptTokens, output: completionTokens })
      
      this.cacheResponse(messages, options || {}, response)
      this.totalResponseTime += Date.now() - startTime
      return response
    } catch (error) {
      this.errorCount++
      throw error
    }
  }

  async generateText(prompt: string, options?: { temperature?: number; maxTokens?: number }): Promise<string> {
    const messages: DeepSeekMessage[] = [
      {
        role: 'system',
        content: '你是谐伴AI助手，一个专业的后台管理系统智能助手。请使用中文进行回复，保持友好、专业的语气。'
      },
      {
        role: 'user',
        content: prompt
      }
    ]

    const response = await this.chat(messages, options)
    return response.choices[0]?.message?.content || ''
  }

  async analyzeData(data: any, prompt: string): Promise<string> {
    const messages: DeepSeekMessage[] = [
      {
        role: 'system',
        content: '你是一位数据分析专家，请基于提供的数据进行深入分析。使用中文回复，提供详细的分析报告和建议。'
      },
      {
        role: 'user',
        content: `数据：\n${JSON.stringify(data, null, 2)}\n\n分析要求：${prompt}`
      }
    ]

    const response = await this.chat(messages)
    return response.choices[0]?.message?.content || ''
  }

  async generateReport(data: any, type: string = 'summary'): Promise<string> {
    const templates: Record<string, string> = {
      summary: '请对以下数据进行综合分析，生成一份详细的总结报告。',
      sales: '请对以下销售数据进行分析，包括趋势、对比、异常点和建议。',
      user: '请对以下用户数据进行分析，包括用户画像、行为特征和增长趋势。',
      performance: '请对以下性能数据进行分析，包括指标评估、瓶颈识别和优化建议。',
      security: '请对以下安全数据进行分析，包括风险评估、漏洞识别和改进建议。'
    }

    const template = templates[type] || templates.summary
    const dataStr = typeof data === 'string' ? data : JSON.stringify(data, null, 2)

    const messages: DeepSeekMessage[] = [
      {
        role: 'system',
        content: '你是一位专业的报告生成专家。请基于提供的数据生成一份结构化的报告，包括分析、结论和建议。'
      },
      {
        role: 'user',
        content: `数据：\n${dataStr}\n\n${template}`
      }
    ]

    const response = await this.chat(messages)
    return response.choices[0]?.message?.content || ''
  }

  async summarizeText(text: string, maxLength: number = 500): Promise<string> {
    const messages: DeepSeekMessage[] = [
      {
        role: 'system',
        content: '你是一位专业的文本摘要专家。请将提供的文本进行简明扼要的总结，保持核心信息完整。'
      },
      {
        role: 'user',
        content: `请总结以下文本（最多${maxLength}字）：\n${text}`
      }
    ]

    const response = await this.chat(messages, { maxTokens: Math.min(maxLength * 2, 1024) })
    return response.choices[0]?.message?.content || ''
  }

  async extractKeywords(text: string, count: number = 10): Promise<string[]> {
    const messages: DeepSeekMessage[] = [
      {
        role: 'system',
        content: '你是一位关键词提取专家。请从提供的文本中提取最重要的关键词，返回一个JSON数组。'
      },
      {
        role: 'user',
        content: `请从以下文本中提取${count}个最重要的关键词：\n${text}\n\n请直接返回JSON数组，不要包含其他内容。`
      }
    ]

    const response = await this.chat(messages, { maxTokens: 512 })
    const content = response.choices[0]?.message?.content || '[]'
    
    try {
      return JSON.parse(content)
    } catch {
      return content.split(/[，,、\s]+/).filter(k => k.trim()).slice(0, count)
    }
  }

  getCacheStats(): { 
    size: number; 
    hits: number; 
    misses: number;
    hitRate: number;
    avgAccessCount: number;
    oldestEntryAge: number;
    newestEntryAge: number;
  } {
    const total = this.cacheHits + this.cacheMisses
    const hitRate = total > 0 ? (this.cacheHits / total) * 100 : 0
    
    let oldestAge = 0
    let newestAge = Infinity
    let totalAccess = 0
    const now = Date.now()
    
    for (const entry of this.cache.values()) {
      const age = now - entry.timestamp
      oldestAge = Math.max(oldestAge, age)
      newestAge = Math.min(newestAge, age)
      totalAccess += entry.accessCount
    }
    
    return {
      size: this.cache.size,
      hits: this.cacheHits,
      misses: this.cacheMisses,
      hitRate: Math.round(hitRate * 100) / 100,
      avgAccessCount: this.cache.size > 0 ? Math.round((totalAccess / this.cache.size) * 100) / 100 : 0,
      oldestEntryAge: Math.round(oldestAge / 1000),
      newestEntryAge: Math.round(newestAge / 1000)
    }
  }

  getPerformanceStats(): {
    totalRequests: number;
    successfulRequests: number;
    errorRate: number;
    avgResponseTime: number;
    cacheHitRate: number;
  } {
    const total = this.cacheHits + this.cacheMisses
    const cacheHitRate = total > 0 ? (this.cacheHits / total) * 100 : 0
    const errorRate = this.requestCount > 0 ? (this.errorCount / this.requestCount) * 100 : 0
    const avgResponseTime = this.requestCount > 0 ? Math.round(this.totalResponseTime / this.requestCount) : 0
    
    return {
      totalRequests: this.requestCount,
      successfulRequests: this.requestCount - this.errorCount,
      errorRate: Math.round(errorRate * 100) / 100,
      avgResponseTime,
      cacheHitRate: Math.round(cacheHitRate * 100) / 100
    }
  }

  logPerformanceReport(): void {
    const stats = this.getPerformanceStats()
    const cacheStats = this.getCacheStats()
    
    console.log('=== DeepSeek Optimized Service Performance Report ===')
    console.log(`Total Requests: ${stats.totalRequests}`)
    console.log(`Successful Requests: ${stats.successfulRequests}`)
    console.log(`Error Rate: ${stats.errorRate}%`)
    console.log(`Average Response Time: ${stats.avgResponseTime}ms`)
    console.log(`Cache Hit Rate: ${stats.cacheHitRate}%`)
    console.log(`Cache Size: ${cacheStats.size}`)
    console.log(`Cache Hits: ${cacheStats.hits}`)
    console.log(`Cache Misses: ${cacheStats.misses}`)
    console.log('=====================================================')
  }

  resetStats(): void {
    this.cacheHits = 0
    this.cacheMisses = 0
    this.requestCount = 0
    this.totalResponseTime = 0
    this.errorCount = 0
    console.log('Performance statistics reset')
  }

  private estimateTokens(messages: DeepSeekMessage[]): number {
    let total = 0
    messages.forEach(msg => {
      total += msg.content.length
      total += 4 // 为每个消息添加固定开销
    })
    // 简单估算：中文字符约2个token，英文字符约1个token
    return Math.ceil(total / 2)
  }

  getCostReport(): {
    totalCost: number
    cacheSavings: number
    suggestions: any[]
  } {
    const stats = costOptimizer.getCostStats()
    const suggestions = costOptimizer.getOptimizationSuggestions()
    
    return {
      totalCost: stats.estimatedCost,
      cacheSavings: stats.cacheSavings,
      suggestions: suggestions,
    }
  }

  exportFullReport(): string {
    return costOptimizer.exportCostReport()
  }

  clearCache(): void {
    this.cache.clear()
    console.log('Cache cleared')
  }

  shutdown(): void {
    if (this.batchTimer) {
      clearTimeout(this.batchTimer)
    }
    this.cache.clear()
    console.log('DeepSeekOptimizedService shutdown completed')
  }
}

export const deepseekOptimizedService = new DeepSeekOptimizedService({
  enableCache: true,
  cacheTTL: 300000,
  enableBatching: true,
  batchTimeout: 100,
  maxBatchSize: 10,
  enableRetry: true,
  maxRetries: 3,
  retryDelay: 1000,
  enableRateLimit: true,
  maxRequestsPerSecond: 10
})

export { DeepSeekOptimizedService }